import React, {useEffect, useMemo, useState} from 'react';
import {HStack, VStack, Text as NBText} from "native-base";
import FoldableSettingSection from "../../../../../common/FoldableSettingSection";
import {t} from "../../../../../../services/i18n";
import AppText from "../../../../../common/AppText";
import {GlobalStyleAttributes} from "../../../../../../styles/GlobalStyles";
import {Colors} from "../../../../../../styles/Colors";
import SimpleInput from "../../../../../common/SimpleInput";
import LiveLinkSetting from "../ReusableComponents/LiveLinkSetting";
import {useDispatch, useSelector} from "react-redux";
import {ARBOX_INVOICES, invoicesTypes, SERIES_FUTURE_SESSIONS_EDIT_TYPE, TRIAL} from "../../../../../../services/ConstHelper";
import IncomeCardSelect from "../../../../Forms/Settings/IncomeCardSelect";
import {optionalSteps} from "../StepOptions";
import {sendDataEvent, resolveEventCategoryType} from "../../../../../../services/Helpers";
import {Radio} from "antd";
import InfoTooltip from "../../../../../common/InfoTooltip";
import usePermissions from '../../../../../../services/Permissions';
import FormInput from '../../../../../common/FormInput';
import MultiSingleMenuWithSearch from '../../../../../common/MultiSingleMenuWithSearch';
import {updateFlags} from "../../../../../../redux/stepperSlice";

const SessionSettings = (props) => {
    const { values, setFieldValue, setValues, errors, displayProps, target } = props;
    const editType = useSelector(state => state.stepper.editType)
    const selectionsTree = useSelector((state) => state.stepper.selectionsTree);
    const eventTypeKey = useMemo(() => {
        const selected = selectionsTree?.[optionalSteps.EVENT_TYPE];
        return resolveEventCategoryType(selected) ?? selected ?? 'class';
    }, [selectionsTree]);
    const isEdit = useSelector(state => state.stepper?.editType);
    const [openedSection, setOpenedSection] = useState(displayProps?.defaultOpenedSection || 'transparentSetting');
    const [ageMode, setAgeMode] = useState(() => (values.min_age != null && values.max_age != null) ? 'age-between' : (values.min_age != null ? 'age-from' : 'no-age-restriction'));
    const box = useSelector((state) => state.box.box);
    const selectedLocation = useMemo(() => {
        if(box && values.locations_box_fk) {
            return box.locations_box.find(l => l.id === values.locations_box_fk)
        }
    },[box, values.locations_box_fk])
    const taxes = useMemo(() => {
        if (selectedLocation && selectedLocation.taxes.length > 0) {
            return selectedLocation.taxes.reduce((acc, tax) => [...acc, {id: tax.id, name: `${tax.name} (${tax.percentage}%)`}],[])
        }
    },[selectedLocation])
    const currencySymbol = useMemo(() => selectedLocation?.currency_symbol,[selectedLocation])
    const allowOnlyDebt = useMemo(() => selectedLocation?.hasPayments === false, [selectedLocation]);
    const hasArboxInvoice = useMemo(() => box?.rivhit_id == invoicesTypes[ARBOX_INVOICES], [box]);

    const { has, hasLock } = usePermissions();

    const cancellationExplainText = useMemo(() => {
        if(values?.disable_cancellation_time) {
            if(values?.enable_late_cancellation) return t('cancellation-allow-late', {hoursNum: values?.disable_cancellation_time})
             else return t('cancellation-up-to', {hoursNum: values?.disable_cancellation_time})
        } else {
            return t('cancellation-always')
        }
    }, [values?.disable_cancellation_time, values?.enable_late_cancellation]);

    const cancellationValueRadio = useMemo(() => {
        if(values?.disable_cancellation_time) {
            if(values?.enable_late_cancellation) return 'with-late-cancellation'
            else return 'up-to'
        } else {
            return 'always'
        }
    }, [values?.disable_cancellation_time, values?.enable_late_cancellation]);

    useEffect(() => {
        if(selectionsTree[optionalSteps.EVENT_TYPE]) {
            sendDataEvent(`add ${selectionsTree[optionalSteps.EVENT_TYPE]} pop up - Settings`);
        }
    }, [selectionsTree[optionalSteps.EVENT_TYPE]]);

    useEffect(() => {
        let paymentOptions = values?.paymentOptions?.length > 0 ? values.paymentOptions : []
        if(!isEdit && displayProps?.showBookForTrialSettings && !values?.paymentOptions?.some(payment => payment.type === TRIAL)) {
            paymentOptions.push({type: TRIAL, show_in_app: 0, price: 0, max_installments: 1, taxes: [], paid: false, redirectLinkEnabled: false, redirectLink: null, income_card: null})
            setFieldValue('paymentOptions', paymentOptions)
        }
    }, [values?.paymentOptions, displayProps]);

    const onSectionToggle = (sectionName) => {
        if(openedSection === sectionName) setOpenedSection(null)
        else setOpenedSection(sectionName)
    }

    const onCancellationRadioSelect = (e) => {
        const selection = e.target.value
        switch (selection) {
            case 'always':
                setFieldValue('disable_cancellation_time', 0)
                setFieldValue('enable_late_cancellation', false)
                break;
            case 'up-to':
                setFieldValue('disable_cancellation_time', 1)
                setFieldValue('enable_late_cancellation', false)
                break;
            case 'with-late-cancellation':
                setFieldValue('disable_cancellation_time', 1)
                setFieldValue('enable_late_cancellation', true)
                break;
            default:
                break;
        }
    }

    const onMinimumUsersRadioSelection = (e) => {
        const selection = e.target.value
        if(selection === 'no-minimum') {
            setFieldValue('cancel_limit', 0)
            setFieldValue('min_users', 0)
        } else {
            setFieldValue('cancel_limit', 1)
            setFieldValue('min_users', 1)
        }
    }

    const onOnlineLinkRadioSelection = (selection) => {
        if(selection === 'in-person') setFieldValue('live_link', null)
        else setFieldValue('live_link', '')
    }

    const onGenderRestrictionRadioSelection = (e) => {
        const selection = e.target.value
        setFieldValue('gender', selection === 'other' ? null : selection)
    }

    const onAgeRestrictionRadioSelection = (e) => {
        const selection = e.target.value
        setAgeMode(selection)
        // Set both fields in a single update so validation runs against a consistent
        // min/max pair (two separate setFieldValue calls validate against stale state
        // and falsely flag min_age as required when max_age is set).
        let nextAge
        switch (selection) {
            case 'age-from':
                nextAge = {min_age: values.min_age ?? 0, max_age: null}
                break;
            case 'age-between':
                nextAge = {min_age: values.min_age ?? 0, max_age: values.max_age ?? ((values.min_age ?? 0) + 1)}
                break;
            default:
                nextAge = {min_age: null, max_age: null}
                break;
        }
        setValues({...values, ...nextAge})
    }

    const genderRadioValue = values.gender || 'other'
    const ageRadioValue = ageMode

    const genderRestrictionSummary = values.gender === 'male'
        ? t('session-gender-males-only')
        : (values.gender === 'female' ? t('session-gender-females-only') : t('session-gender-no-restriction'))

    const ageRestrictionSummary = ageMode === 'age-between'
        ? t('session-age-between-summary', {minAge: values.min_age, maxAge: values.max_age})
        : (ageMode === 'age-from' ? t('session-age-from-summary', {minAge: values.min_age}) : t('session-age-no-restriction'))

    const setValue = (name, value, shouldParseInt = false) => {
        setFieldValue(name, shouldParseInt ? (isNaN(parseInt(value)) ? '' : parseInt(value)) : value)
    }

    // Age inputs accept decimals (e.g. 0.3, 1.5). Empty/invalid falls back to 0; negative snaps to 0.
    const setAgeValue = (name, value) => {
        const parsed = parseFloat(value)
        setFieldValue(name, (isNaN(parsed) || parsed < 0) ? 0 : parsed)
    }

    const getSeriesNameAttribute = () => {
        if(values?.repeatConfig && 'seriesName' in values.repeatConfig) return values.repeatConfig.seriesName
        if(values?.multiDayConfig && 'seriesName' in values.multiDayConfig) return values.multiDayConfig.seriesName
        if(target?.series && editType === SERIES_FUTURE_SESSIONS_EDIT_TYPE) return values.series_name
    }

    const getSeriesNameAttributeName = () => {
        if(values?.repeatConfig && 'seriesName' in values.repeatConfig) return 'repeatConfig.seriesName'
        if(values?.multiDayConfig && 'seriesName' in values.multiDayConfig) return 'multiDayConfig.seriesName'
        if(target?.series && editType === SERIES_FUTURE_SESSIONS_EDIT_TYPE) return 'series_name'
    }

    const handleTrialClassOption = (option) => {
        const trialIndex = findPaymentTypeIndex(TRIAL)
        if(trialIndex < 0) return null
        if(option === 'manage-trial-class') {
            setFieldValue(`paymentOptions[${trialIndex}].show_in_app`, 0)
            redirectLinkRadioChange('no-redirect', trialIndex)
        } else {
            setFieldValue(`paymentOptions[${trialIndex}].show_in_app`, 1)
        }
    }

    const redirectLinkRadioChange = (radio, trialIndex) => {
        const allowed = radio === 'with-redirect'
        setFieldValue(`paymentOptions[${trialIndex}].redirectLinkEnabled`, allowed)
        if(!allowed) setFieldValue(`paymentOptions[${trialIndex}].redirectLink`, null)
    }

    const findPaymentTypeIndex = (type) => {
        return values?.paymentOptions?.findIndex(option => option.type === type)
    }

    const renderTrialSection = () => {
        const trialIndex = findPaymentTypeIndex(TRIAL)
        const selectedOption = trialIndex === -1 || values?.paymentOptions?.[trialIndex].show_in_app === 0 ? 'manage-trial-class' : 'manage-site-trial-class'
        const getSummary = () => {
            let key = 'explain-trial-class-manage'
            if(values?.paymentOptions?.[trialIndex]?.show_in_app) {
                key = 'explain-trial-class-manage-site'
                if(has('siteSuccessRedirect')) {
                    if(values.paymentOptions[trialIndex].redirectLinkEnabled) {
                        key += '-with-redirect'
                    } else {
                        key += '-no-redirect'
                    }
                }
            }
            return t(key, {price: `${values?.paymentOptions?.[trialIndex]?.price || 0}${currencySymbol}`})
        }
        return (
            <FoldableSettingSection header={t('enable-trial-class-on-site')} infoTooltip={t(`enable-trial-class-tooltip${editType ? '-edit' : ''}`)}
                                    isOpen={openedSection === 'trialClassSiteSetting'} onToggle={() => onSectionToggle('trialClassSiteSetting')}
                                    hasLock={hasLock('bookForTrialClassSite')} summary={getSummary()}>
                <VStack space={'0.5rem'}>
                    <VStack>
                        <FormInput
                            value={values?.paymentOptions?.[trialIndex]?.price}
                            onChangeCallback={(val) => setFieldValue(`paymentOptions[${trialIndex}].price`, val)}
                            label={t('price')}
                            width={'6rem'}
                            formatAddon={currencySymbol}
                            inputType={'float'}
                            required={true}
                            containerProps={{mb: 0}}
                            errorHeight={'unset'}
                        />
                        {taxes && <VStack space={'0.2rem'} w={'12rem'}>
                            <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('taxes')}</AppText>
                            <MultiSingleMenuWithSearch options={taxes} onChange={(val) => setFieldValue(`paymentOptions[${trialIndex}].taxes`, val)} placeholder={t('taxSelectorLabel')} defaultValue={values?.paymentOptions?.[trialIndex]?.taxes}/>
                        </VStack>}
                        {hasArboxInvoice && has('accountCardsSettings') && values.locations_box_fk &&
                            <VStack mt={'10px'}>
                                <IncomeCardSelect
                                    label={t('series-payments-TRIAL-account-card')}
                                    locationId={values.locations_box_fk}
                                    value={values?.paymentOptions?.[trialIndex]?.income_card}
                                    onChange={(val) => setFieldValue(`paymentOptions[${trialIndex}].income_card`, val)}
                                />
                            </VStack>
                        }
                    </VStack>
                    <VStack space={'0.2rem'} flex={1}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('display-on')}</AppText>
                        <Radio.Group name={'trialClassSiteSetting-radio'} onChange={e => handleTrialClassOption(e.target.value)} value={selectedOption}>
                            <VStack space={'0.5rem'}>
                                <Radio value="manage-trial-class" size="sm"><AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('no-trial-class-site')}</AppText></Radio>
                                <Radio value="manage-site-trial-class" size="sm">
                                    <HStack><AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t(`${allowOnlyDebt ? 'debt-' : ''}allow-trial-class-manage-site`)}</AppText></HStack>
                                </Radio>
                            </VStack>
                        </Radio.Group>
                    </VStack>
                    {values?.paymentOptions?.[trialIndex]?.show_in_app > 0 && has('siteSuccessRedirect') &&
                        <VStack space={'0.2rem'} flex={1}>
                            <HStack alignItems={'center'}>
                                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('redirectMTLabel')}</AppText>
                                <InfoTooltip title={t('redirectMTTooltip')}/>
                            </HStack>
                            <Radio.Group name={'trialClassSiteSetting-radio'} onChange={e => redirectLinkRadioChange(e.target.value, trialIndex)} value={values.paymentOptions[trialIndex].redirectLinkEnabled ? 'with-redirect' : 'no-redirect'}>
                                <VStack space={'0.5rem'} w={'100%'}>
                                    <Radio value="no-redirect" size="sm"><AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('no-redirect-label')}</AppText></Radio>
                                    <Radio value="with-redirect" size="sm">
                                        <HStack><AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('with-redirect-label')}</AppText></HStack>
                                    </Radio>
                                    {values.paymentOptions[trialIndex].redirectLinkEnabled && <SimpleInput value={values.paymentOptions[trialIndex].redirectLink} onChangeCallback={val => setFieldValue(`paymentOptions[${trialIndex}].redirectLink`, val)} status={errors?.paymentOptions?.[trialIndex]?.redirectLink ? 'error' : null} placeholder={t('redirectEventTypePlaceholder')}/>}
                                </VStack>
                            </Radio.Group>
                        </VStack>
                    }
                </VStack>
            </FoldableSettingSection>
        )
    }

    return (
        <VStack space={'0.5rem'}>
            {displayProps?.showTransparentSettings &&
            <FoldableSettingSection header={t('private-or-public')}
                                    isOpen={openedSection === 'transparentSetting'}
                                    onToggle={() => onSectionToggle('transparentSetting')}
                                    summary={t(`transparent-${!!values.transparent}`)}
            >
                <VStack space={'1rem'}>
                    <Radio.Group name={'transparentSetting-radio'}
                                 onChange={e => setFieldValue('transparent', e.target.value === 'transparent')}
                                 value={values.transparent ? 'transparent' : 'not-transparent'}>
                        <VStack space={'1rem'}>
                            <Radio value="not-transparent" size="sm"><AppText
                                fontSize={GlobalStyleAttributes.fonts.heading3}
                                color={Colors.newDesign.mainText}>{t('transparent-radio-false')}</AppText></Radio>
                            <Radio value="transparent" size="sm"><AppText
                                fontSize={GlobalStyleAttributes.fonts.heading3}
                                color={Colors.newDesign.mainText}>{t('transparent-radio-true')}</AppText></Radio>
                        </VStack>
                    </Radio.Group>
                </VStack>
            </FoldableSettingSection>
            }
            {displayProps?.showSpots && has('hasSpots') &&
                <FoldableSettingSection header={t('class-has-spots')}
                                        isOpen={openedSection === 'spotSettings'}
                                        onToggle={() => onSectionToggle('spotSettings')}
                                        summary={values.has_spots ? t('has-spots-description-true') : t('has-spots-description-false')}
                >
                    <VStack space={'1rem'}>
                        <Radio.Group name={'spotSettings-radio'}
                                     onChange={e => {
                                         const selectedValue = parseInt(e.target.value); // Convert string to integer
                                         setFieldValue('has_spots', selectedValue === 1); // Update based on the numeric value
                                     }}
                                     value={values.has_spots ? 1 : 0}>
                            <VStack space={'1rem'}>
                                <Radio value={0} size="sm"><AppText
                                    fontSize={GlobalStyleAttributes.fonts.heading3}
                                    color={Colors.newDesign.mainText}>{t('has-spots-radio-false')}</AppText></Radio>
                                <Radio value={1} size="sm"><AppText
                                    fontSize={GlobalStyleAttributes.fonts.heading3}
                                    color={Colors.newDesign.mainText}>{t('has-spots-radio-true')}</AppText></Radio>
                            </VStack>
                        </Radio.Group>
                    </VStack>
                </FoldableSettingSection>
            }
            {displayProps?.showBookForTrialSettings && has('bookForTrialClassSite') && renderTrialSection()}
            {displayProps?.showCancellationSettings &&
            <FoldableSettingSection header={t('cancellation')}
                                    isOpen={openedSection === 'cancellationSetting'}
                                    onToggle={() => onSectionToggle('cancellationSetting')}
                                    summary={cancellationExplainText}
            >
                <VStack space={'1rem'}>
                    <Radio.Group name={'cancellationSetting-radio'} onChange={onCancellationRadioSelect} value={cancellationValueRadio}>
                        <VStack space={'1rem'}>
                            <Radio value="always" size="sm">
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('cancellation-radio-allow-always')}</AppText>
                            </Radio>
                            <Radio value="up-to" size="sm">
                                <NBText style={{direction: 'inherit'}}>
                                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('cancellation-radio-allow-up-to')}</AppText>
                                    <SimpleInput value={cancellationValueRadio === 'up-to' ? values.disable_cancellation_time : 0}
                                                 onChangeCallback={(val) => setValue('disable_cancellation_time', val, false)}
                                                 inputType={'float'}
                                                 width={'4rem'}
                                                 styleOverride={{margin: '0 0.5rem'}}
                                    />
                                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('before-session')}</AppText>
                                </NBText>
                            </Radio>
                            <Radio value="with-late-cancellation" size="sm" >
                                <HStack alignItems={'center'}>
                                    <NBText style={{direction: 'inherit'}}>
                                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('cancellation-radio-allow-late')}</AppText>
                                        <SimpleInput value={cancellationValueRadio === 'with-late-cancellation' ? values.disable_cancellation_time : 0}
                                                     onChangeCallback={(val) => setValue('disable_cancellation_time', val, false)}
                                                     inputType={'float'}
                                                     width={'4rem'}
                                                     styleOverride={{margin: '0 0.5rem'}}
                                        />
                                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('before-session')}</AppText>
                                    </NBText>
                                    <InfoTooltip title={t('late-cancellation-tooltip')}/>
                                </HStack>
                            </Radio>
                        </VStack>
                    </Radio.Group>
                </VStack>
            </FoldableSettingSection>
            }
            {displayProps?.showRegistrationRestrictionsSettings &&
            <FoldableSettingSection header={t('registration-restrictions')}
                                    isOpen={openedSection === 'registrationSetting'}
                                    onToggle={() => onSectionToggle('registrationSetting')}
                                    summary={t(values.enable_registration_time ? 'registration-restrictions-open' : 'no-registration-restrictions', {hoursNum: values.enable_registration_time})}
            >
                <VStack space={'1rem'}>
                    <Radio.Group name={'registrationSetting-radio'}
                                 onChange={e => setFieldValue('enable_registration_time', e.target.value === 'no-restrictions' ? 0 : 1)}
                                 value={values.enable_registration_time ? 'restrict-registration' : 'no-restrictions'}>
                        <VStack space={'1rem'}>
                            <Radio value="no-restrictions" size="sm"><AppText
                                fontSize={GlobalStyleAttributes.fonts.heading3}
                                color={Colors.newDesign.mainText}>{t('no-registration-restrictions-radio')}</AppText></Radio>
                            <Radio value="restrict-registration" size="sm">
                                <NBText style={{direction: 'inherit'}}>
                                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('registration-restrictions-radio-open')}</AppText>
                                    <SimpleInput value={values.enable_registration_time}
                                                 onChangeCallback={(val) => setValue('enable_registration_time', val, false)}
                                                 width={'4rem'}
                                                 inputType={'float'}
                                                 styleOverride={{margin: '0 0.5rem'}}
                                    />
                                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('before-it-begins')}</AppText>
                                </NBText>
                            </Radio>
                        </VStack>
                    </Radio.Group>
                </VStack>
            </FoldableSettingSection>
            }
            {displayProps?.showRegistrationBlockSettings &&
            <FoldableSettingSection header={t('registration-block-time')}
                                    isOpen={openedSection === 'registrationBlockSetting'}
                                    onToggle={() => onSectionToggle('registrationBlockSetting')}
                                    summary={t(values.block_registration_time ? 'registration-time-block-open' : 'no-registration-time-block', {hoursNum: values.block_registration_time})}
                                    error={errors.block_registration_time ? t(errors.block_registration_time) : null}
            >
                <VStack space={'1rem'}>
                    <Radio.Group name={'registrationBlockSetting-radio'}
                                 onChange={e => setFieldValue('block_registration_time', e.target.value === 'no-registration-time-block' ? 0 : 1)}
                                 value={values.block_registration_time ? 'registration-time-block' : 'no-registration-time-block'}>
                            <VStack space={'1rem'}>
                                <Radio value='no-registration-time-block' size="sm"><AppText
                                    fontSize={GlobalStyleAttributes.fonts.heading3}
                                    color={Colors.newDesign.mainText}>{t('no-registration-time-block-radio')}</AppText></Radio>
                                <Radio value="registration-time-block" size="sm">
                                    <NBText style={{direction: 'inherit'}}>
                                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('registration-time-block-radio-open')}</AppText>
                                        <SimpleInput value={values.block_registration_time}
                                                     onChangeCallback={(val) => setValue('block_registration_time', val, false)}
                                                     inputType={'float'}
                                                     width={'4rem'}
                                                     styleOverride={{margin: '0 0.5rem'}}
                                        />
                                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('before-it-begins')}</AppText>
                                    </NBText>
                                </Radio>
                            </VStack>
                        </Radio.Group>
                </VStack>
            </FoldableSettingSection>


            }
            {displayProps?.showMinimumParticipantsSettings &&
            <FoldableSettingSection header={t('minimumParticipants')}
                                    isOpen={openedSection === 'minimumParticipantsSetting'}
                                    onToggle={() => onSectionToggle('minimumParticipantsSetting')}
                                    summary={t(!values.cancel_limit ? 'no-minimum' : 'minimum-restriction-open')}
                                    infoTooltip={t('minimum-participants-tooltip')}
            >
                <VStack space={'1rem'}>
                    <Radio.Group name={'minimumParticipantsSetting-radio'} onChange={onMinimumUsersRadioSelection} value={values.min_users && values.cancel_limit ? 'restrict-minimum' : 'no-minimum'}>
                        <VStack space={'1rem'}>
                            <Radio value="no-minimum" size="sm">
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('no-minimum-radio')}</AppText></Radio>
                            <Radio value="restrict-minimum" size="sm">
                                <VStack flex={1}>
                                    <HStack space={'0.5rem'} alignItems={'center'}>
                                        <NBText style={{direction: 'inherit'}}>
                                            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('minimum-restriction-open-radio')}</AppText>
                                            <SimpleInput value={values.min_users}
                                                         onChangeCallback={(val) => setValue('min_users', val, true)}
                                                         numericOnly={true}
                                                         width={'4rem'}
                                                         styleOverride={{margin: '0 0.5rem'}}
                                            />
                                        </NBText>

                                    </HStack>
                                    <HStack space={'0.5rem'} alignItems={'center'}>
                                        <NBText style={{direction: 'inherit'}}>
                                            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('upTo')}</AppText>
                                            <SimpleInput value={values.cancel_limit}
                                                         onChangeCallback={(val) => setValue('cancel_limit', val, true)}
                                                         numericOnly={true}
                                                         width={'4rem'}
                                                         styleOverride={{margin: '0 0.5rem'}}
                                            />
                                            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('before-session')}</AppText>
                                        </NBText>
                                    </HStack>
                                </VStack>
                            </Radio>
                        </VStack>
                    </Radio.Group>
                </VStack>
            </FoldableSettingSection>
            }
            {displayProps?.showGenderRestrictionSettings &&
            <FoldableSettingSection header={t('session-gender-restriction')}
                                    isOpen={openedSection === 'genderRestrictionSetting'}
                                    onToggle={() => onSectionToggle('genderRestrictionSetting')}
                                    summary={genderRestrictionSummary}
                                    infoTooltip={t('session-gender-restriction-tooltip')}
            >
                <VStack space={'1rem'}>
                    <Radio.Group name={'genderRestrictionSetting-radio'} onChange={onGenderRestrictionRadioSelection} value={genderRadioValue}>
                        <VStack space={'1rem'}>
                            <Radio value="other" size="sm">
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('session-gender-no-restriction-radio')}</AppText></Radio>
                            <Radio value="male" size="sm">
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('session-gender-males-only-radio')}</AppText></Radio>
                            <Radio value="female" size="sm">
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('session-gender-females-only-radio')}</AppText></Radio>
                        </VStack>
                    </Radio.Group>
                </VStack>
            </FoldableSettingSection>
            }
            {displayProps?.showAgeRestrictionSettings &&
            <FoldableSettingSection header={t('session-age-restriction')}
                                    isOpen={openedSection === 'ageRestrictionSetting'}
                                    onToggle={() => onSectionToggle('ageRestrictionSetting')}
                                    summary={ageRestrictionSummary}
                                    infoTooltip={t('session-age-restriction-tooltip')}
                                    error={(errors.min_age || errors.max_age) ? (errors.min_age || errors.max_age) : null}
            >
                <VStack space={'1rem'}>
                    <Radio.Group name={'ageRestrictionSetting-radio'} onChange={onAgeRestrictionRadioSelection} value={ageRadioValue}>
                        <VStack space={'1rem'}>
                            <Radio value="no-age-restriction" size="sm">
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('session-age-no-restriction-radio')}</AppText></Radio>
                            <Radio value="age-from" size="sm">
                                <VStack flex={1}>
                                    <HStack space={'0.5rem'} alignItems={'center'}>
                                        <NBText style={{direction: 'inherit'}}>
                                            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('session-age-from-radio')}</AppText>
                                            <SimpleInput key={`age-from-min-${ageRadioValue}`}
                                                         value={ageRadioValue === 'age-from' ? values.min_age : ''}
                                                         onChangeCallback={(val) => setAgeValue('min_age', val)}
                                                         inputType={'float'}
                                                         width={'4rem'}
                                                         styleOverride={{margin: '0 0.5rem'}}
                                            />
                                        </NBText>
                                    </HStack>
                                </VStack>
                            </Radio>
                            <Radio value="age-between" size="sm">
                                <VStack flex={1}>
                                    <HStack space={'0.5rem'} alignItems={'center'}>
                                        <NBText style={{direction: 'inherit'}}>
                                            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('session-age-between-radio')}</AppText>
                                            <SimpleInput key={`age-between-min-${ageRadioValue}`}
                                                         value={ageRadioValue === 'age-between' ? values.min_age : ''}
                                                         onChangeCallback={(val) => setAgeValue('min_age', val)}
                                                         inputType={'float'}
                                                         width={'4rem'}
                                                         styleOverride={{margin: '0 0.5rem'}}
                                            />
                                            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('session-age-to')}</AppText>
                                            <SimpleInput key={`age-between-max-${ageRadioValue}`}
                                                         value={ageRadioValue === 'age-between' ? values.max_age : ''}
                                                         onChangeCallback={(val) => setAgeValue('max_age', val)}
                                                         inputType={'float'}
                                                         width={'4rem'}
                                                         styleOverride={{margin: '0 0.5rem'}}
                                            />
                                        </NBText>
                                    </HStack>
                                </VStack>
                            </Radio>
                        </VStack>
                    </Radio.Group>
                </VStack>
            </FoldableSettingSection>
            }
            {displayProps?.showSeriesNameSettings &&
                <FoldableSettingSection header={t(`${eventTypeKey}SeriesName`)}
                                        isOpen={openedSection === 'seriesNameSetting'}
                                        onToggle={() => onSectionToggle('seriesNameSetting')}
                                        summary={t(`series-name-${eventTypeKey ? `${eventTypeKey}-` : ''}${getSeriesNameAttribute() ? '' : 'dont-'}exist`)}
                >
                    <VStack>
                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t(`series-name-explain-${eventTypeKey}`)}</AppText>
                        <SimpleInput value={getSeriesNameAttribute()}
                                     onChangeCallback={(val) => setValue(getSeriesNameAttributeName(), val)}
                        />
                    </VStack>
                </FoldableSettingSection>
            }
            {displayProps?.showGroupRegisterSettings &&
            <FoldableSettingSection header={t('groupRegisterSetting')}
                                    isOpen={openedSection === 'groupRegisterSetting'}
                                    onToggle={() => onSectionToggle('groupRegisterSetting')}
                                    summary={t(`groupRegister-${values.register_group_member ? 'on' : 'off'}`)}
            >
                <VStack space={'1rem'}>
                    <Radio.Group name={'groupRegisterSetting-radio'}
                                 onChange={e => setFieldValue('register_group_member', e.target.value === 'allow-group')}
                                 value={values.register_group_member ? 'allow-group' : 'dont-allow-group'}>
                        <VStack space={'1rem'}>
                            <Radio value="dont-allow-group" size="sm"><AppText
                                fontSize={GlobalStyleAttributes.fonts.heading3}
                                color={Colors.newDesign.mainText}>{t('groupRegister-off-radio')}</AppText></Radio>
                            <Radio value="allow-group" size="sm"><AppText
                                fontSize={GlobalStyleAttributes.fonts.heading3}
                                color={Colors.newDesign.mainText}>{t('groupRegister-on-radio')}</AppText></Radio>
                        </VStack>
                    </Radio.Group>
                </VStack>
            </FoldableSettingSection>
            }
            {displayProps?.showMidBookingSettings &&
                <FoldableSettingSection header={t('hugMidBookingSettingHeader')}
                                        isOpen={openedSection === 'midBookingSettings'}
                                        onToggle={() => onSectionToggle('midBookingSettings')}
                                        summary={t(`midBooking-${values.allow_mid_booking ? 'on' : 'off'}`)}
                >
                    <VStack space={'1rem'}>
                        <Radio.Group name={'idBookingSettings-radio'}
                                     onChange={e => setFieldValue('allow_mid_booking', e.target.value === 'allow-mid-booking')}
                                     value={values.allow_mid_booking ? 'allow-mid-booking' : 'dont-allow-mid-booking'}>
                            <VStack space={'1rem'}>
                                <Radio value="dont-allow-mid-booking" size="sm"><AppText
                                    fontSize={GlobalStyleAttributes.fonts.heading3}
                                    color={Colors.newDesign.mainText}>{t('mid-booking-off-radio')}</AppText></Radio>
                                <Radio value="allow-mid-booking" size="sm"><AppText
                                    fontSize={GlobalStyleAttributes.fonts.heading3}
                                    color={Colors.newDesign.mainText}>{t('mid-booking-on-radio')}</AppText></Radio>
                            </VStack>
                        </Radio.Group>
                    </VStack>
                </FoldableSettingSection>
            }
            {displayProps?.showOnlineSessionSettings &&
            <FoldableSettingSection header={t(`online-${eventTypeKey ?? 'session'}`)}
                                    isOpen={openedSection === 'onlineSetting'}
                                    onToggle={() => onSectionToggle('onlineSetting')}
                                    summary={t(values.live_link ? `online-${eventTypeKey ?? 'session'}-open` : `personal-${eventTypeKey ?? 'session'}-radio`)}
            >
                <VStack space={'1rem'}>
                    <LiveLinkSetting selectionValue={values.live_link === null ? 'in-person' : 'online'}
                                     inputValue={values.live_link} onChangeSelection={onOnlineLinkRadioSelection}
                                     onChangeText={(val) => setValue('live_link', val)}
                    />
                </VStack>
            </FoldableSettingSection>
            }
        </VStack>
    );
};

export default SessionSettings;
